// Cognito Hosted UI 认证（OIDC：Authorization Code + PKCE，公共客户端无密钥）。
//
// 职责：
//  - 构建 Hosted UI authorize URL（带 PKCE code_challenge=S256 与 state）。
//  - 处理回调：用授权码到 Cognito token endpoint 交换 token。
//  - 存储 token：access/id 内存保存 + 过期判断；refresh token 短存于 sessionStorage
//    （会话级、关闭标签即清除，符合 design.md「内存 / 短存」与凭证安全表）。
//  - 静默刷新（refresh_token grant）与登出（Hosted UI /logout）。
//
// 设计依据：design.md「PWA 设计 → 认证」、Components 3；infra 任务 4.5。
// Requirements: 2.1, 2.2, 2.6, 11.4。

import {
  createPkceChallenge,
  deriveCodeChallenge,
} from "./pkce.js";
import {
  isAuthConfigured,
  resolveAuthConfig,
  type AuthConfig,
} from "./config.js";

/** Cognito token endpoint 返回结构（authorization_code / refresh_token grant）。 */
interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number; // 秒
  token_type: string;
}

/** 进程内持有的会话 token 集合。 */
export interface TokenSet {
  readonly accessToken: string;
  readonly idToken?: string;
  readonly refreshToken?: string;
  /** access token 过期的绝对时间戳（epoch ms）。 */
  readonly expiresAt: number;
}

// PKCE / state 暂存键（sessionStorage，仅在登录跳转往返期间存活）。
const STORAGE_VERIFIER = "comfy.pkce.verifier";
const STORAGE_STATE = "comfy.oauth.state";
// refresh token 短存键（会话级）。
const STORAGE_REFRESH = "comfy.auth.refresh";

// access token 过期前的提前量（ms）：提前视为过期以避免临界请求 401。
const EXPIRY_SKEW_MS = 30_000;

/**
 * CognitoAuth：封装 Hosted UI OIDC + PKCE 全流程的轻量客户端。
 * access/id token 仅存内存；refresh token 存 sessionStorage（短存）。
 */
export class CognitoAuth {
  private readonly cfg: AuthConfig;
  private tokens: TokenSet | null = null;
  // 防并发刷新：复用同一个 in-flight 刷新 Promise。
  private refreshInFlight: Promise<TokenSet | null> | null = null;

  constructor(cfg: AuthConfig = resolveAuthConfig()) {
    this.cfg = cfg;
    // 启动时尝试恢复 refresh token（页面刷新后静默续期）。
    const stored = safeSessionGet(STORAGE_REFRESH);
    if (stored) {
      this.tokens = {
        accessToken: "",
        refreshToken: stored,
        expiresAt: 0, // 立即过期 → 首次取 token 触发 refresh
      };
    }
  }

  get config(): AuthConfig {
    return this.cfg;
  }

  isConfigured(): boolean {
    return isAuthConfigured(this.cfg);
  }

  /** 是否持有未过期的 access token。 */
  isAuthenticated(): boolean {
    return Boolean(
      this.tokens?.accessToken &&
        this.tokens.expiresAt - EXPIRY_SKEW_MS > Date.now(),
    );
  }

  /** 当前 token 集（可能为 null 或已过期）。 */
  getTokens(): TokenSet | null {
    return this.tokens;
  }

  /**
   * 跳转到 Hosted UI 登录页：先生成 PKCE/state 暂存于 sessionStorage，
   * 再 location 跳转 authorize URL。
   */
  async login(): Promise<void> {
    const url = await this.buildAuthorizeUrl();
    if (typeof window !== "undefined") {
      window.location.assign(url);
    }
  }

  /**
   * 构建 Hosted UI authorize URL（response_type=code + PKCE S256 + state）。
   * 同时把 verifier/state 写入 sessionStorage 供回调校验与换取。
   */
  async buildAuthorizeUrl(): Promise<string> {
    if (!this.isConfigured()) {
      throw new Error("Cognito 未配置：缺少 VITE_COGNITO_DOMAIN / VITE_USER_POOL_CLIENT_ID");
    }
    const { verifier, challenge, state } = await createPkceChallenge();
    safeSessionSet(STORAGE_VERIFIER, verifier);
    safeSessionSet(STORAGE_STATE, state);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.cfg.clientId,
      redirect_uri: this.cfg.redirectUri,
      scope: this.cfg.scopes.join(" "),
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });
    return `${this.cfg.cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * 判断当前 URL 是否为 OAuth 回调（含 code 参数）。
   */
  static isCallbackUrl(search: string): boolean {
    return new URLSearchParams(search).has("code");
  }

  /**
   * 处理回调：校验 state，用 code + code_verifier 换取 token。
   * @param search location.search（含 code/state）。
   * @returns 换取到的 TokenSet。
   */
  async handleRedirectCallback(
    search: string = typeof window !== "undefined" ? window.location.search : "",
  ): Promise<TokenSet> {
    const params = new URLSearchParams(search);
    const error = params.get("error");
    if (error) {
      throw new Error(
        `Cognito 登录失败：${error} ${params.get("error_description") ?? ""}`.trim(),
      );
    }
    const code = params.get("code");
    const returnedState = params.get("state");
    if (!code) {
      throw new Error("回调缺少授权码（code）");
    }
    const expectedState = safeSessionGet(STORAGE_STATE);
    if (expectedState && returnedState !== expectedState) {
      throw new Error("OAuth state 不匹配，疑似 CSRF，拒绝处理");
    }
    const verifier = safeSessionGet(STORAGE_VERIFIER);
    if (!verifier) {
      throw new Error("缺少 PKCE code_verifier，无法换取 token");
    }

    const tokens = await this.exchangeCodeForTokens(code, verifier);
    // 清理一次性 PKCE/state。
    safeSessionRemove(STORAGE_VERIFIER);
    safeSessionRemove(STORAGE_STATE);
    return tokens;
  }

  private async exchangeCodeForTokens(
    code: string,
    verifier: string,
  ): Promise<TokenSet> {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.cfg.clientId,
      code,
      redirect_uri: this.cfg.redirectUri,
      code_verifier: verifier,
    });
    const res = await fetch(`${this.cfg.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      const detail = await safeText(res);
      throw new Error(`token 交换失败（${res.status}）：${detail}`);
    }
    const json = (await res.json()) as TokenResponse;
    return this.storeTokenResponse(json);
  }

  /**
   * 用 refresh token 续期 access/id token。无 refresh token 则返回 null。
   * 并发调用会复用同一 in-flight Promise。
   */
  async refresh(): Promise<TokenSet | null> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const refreshToken = this.tokens?.refreshToken;
    if (!refreshToken) return null;

    this.refreshInFlight = (async () => {
      try {
        const body = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: this.cfg.clientId,
          refresh_token: refreshToken,
        });
        const res = await fetch(`${this.cfg.cognitoDomain}/oauth2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        if (!res.ok) {
          // refresh 失败（过期/吊销）→ 清理，要求重新登录。
          this.clearSession();
          return null;
        }
        const json = (await res.json()) as TokenResponse;
        // refresh grant 通常不返回新的 refresh_token，沿用旧值。
        if (!json.refresh_token) json.refresh_token = refreshToken;
        return this.storeTokenResponse(json);
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  /**
   * 取一个有效 access token：未过期直接返回；将过期则尝试 refresh。
   * 无法取得有效 token 时返回 null（调用方应触发重新登录）。
   */
  async getValidAccessToken(): Promise<string | null> {
    if (this.isAuthenticated()) {
      return this.tokens!.accessToken;
    }
    const refreshed = await this.refresh();
    return refreshed?.accessToken ?? null;
  }

  /** 登出：清本地会话并跳转 Hosted UI /logout。 */
  logout(): void {
    this.clearSession();
    if (!this.isConfigured() || typeof window === "undefined") return;
    const params = new URLSearchParams({
      client_id: this.cfg.clientId,
      logout_uri: this.cfg.logoutRedirectUri,
    });
    window.location.assign(`${this.cfg.cognitoDomain}/logout?${params.toString()}`);
  }

  /** 清除内存与短存中的全部 token。 */
  clearSession(): void {
    this.tokens = null;
    safeSessionRemove(STORAGE_REFRESH);
  }

  private storeTokenResponse(json: TokenResponse): TokenSet {
    const expiresAt = Date.now() + json.expires_in * 1000;
    const tokens: TokenSet = {
      accessToken: json.access_token,
      idToken: json.id_token,
      refreshToken: json.refresh_token,
      expiresAt,
    };
    this.tokens = tokens;
    if (tokens.refreshToken) {
      safeSessionSet(STORAGE_REFRESH, tokens.refreshToken);
    }
    return tokens;
  }
}

// ── sessionStorage 安全包装（SSR / 测试环境无 window 时降级为 no-op）──────────

function safeSessionGet(key: string): string | null {
  try {
    return typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(key, value);
  } catch {
    /* 忽略隐私模式写入失败 */
  }
}

function safeSessionRemove(key: string): void {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(key);
  } catch {
    /* no-op */
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

// 重新导出 PKCE 校验助手，便于在他处复用（如调试）。
export { deriveCodeChallenge };
